/**
 * Pack Opening — open OGN/SFD packs, add to collection
 *
 * Cards start face-down with a rarity glow hinting at their treatment.
 * Click each card to flip-reveal it, or "Reveal All" to see everything.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchJson, postJson, putJson } from './api.js';
import CardTile from './CardTile.jsx';
import CardDetailModal from './CardDetailModal.jsx';
import { groupCardsById } from './cardUtils.js';

const OPENABLE_SETS = [
  { code: 'OGN', label: 'Origins', img: '/pack-ogn.jpg' },
  { code: 'SFD', label: 'Spiritforged', img: '/pack-sfd.jpg' }
];

const CARD_BACK = '/card-back.png';

import { readPriceSettings } from './priceUtils.js';

/** Human-readable treatment label */
function treatmentLabel(t) {
  if (!t) return null;
  const map = {
    foil: 'Foil', rare: 'Rare', epic: 'Epic',
    alt_art: 'Alt Art', showcase: 'Showcase',
    signed: 'Signed', showcase_rune: 'Showcase Rune'
  };
  return map[t] || t;
}

/** Map treatment to a glow CSS class */
function glowClass(treatment) {
  if (!treatment) return 'glow-common';
  switch (treatment) {
    case 'signed': return 'glow-signed';
    case 'showcase': return 'glow-showcase';
    case 'showcase_rune': return 'glow-showcase';
    case 'alt_art': return 'glow-alt-art';
    case 'epic': return 'glow-epic';
    case 'rare': return 'glow-rare';
    case 'foil': return 'glow-foil';
    default: return 'glow-common';
  }
}

export default function PackOpening() {
  const [slots, setSlots] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastSet, setLastSet] = useState(null);
  const [error, setError] = useState(null);
  const [sessionCards, setSessionCards] = useState([]);
  const [showDramatic, setShowDramatic] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [revealedSlots, setRevealedSlots] = useState(new Set());
  const [{ showPrices, pricePeriod }] = useState(() => readPriceSettings());

  const orderedSlots = useMemo(
    () => (slots ? [...slots].sort((a, b) => (a.slot || 0) - (b.slot || 0)) : []),
    [slots]
  );

  // Split slots into rows: 1-5, 6-10, 11-13, 14
  const row1 = orderedSlots.filter((s) => s.slot >= 1 && s.slot <= 5);
  const row2 = orderedSlots.filter((s) => s.slot >= 6 && s.slot <= 10);
  const row3 = orderedSlots.filter((s) => s.slot >= 11 && s.slot <= 13);
  const slotRune = orderedSlots.find((s) => s.slot === 14);

  const allRevealed = orderedSlots.length > 0 && orderedSlots.every((s) => revealedSlots.has(s.slot));

  const packTally = (() => {
    if (!orderedSlots.length) return null;
    const slot11 = orderedSlots.find((s) => s.slot === 11);
    const rareSlots = orderedSlots.filter((s) => s.slot === 12 || s.slot === 13);
    const slot14 = orderedSlots.find((s) => s.slot === 14);
    return {
      slot11Treatment: slot11?.treatment || null,
      signed: rareSlots.filter((s) => s.treatment === 'signed').length,
      showcase: rareSlots.filter((s) => s.treatment === 'showcase').length,
      altArt: rareSlots.filter((s) => s.treatment === 'alt_art').length,
      epic: rareSlots.filter((s) => s.treatment === 'epic').length,
      rare: rareSlots.filter((s) => s.treatment === 'rare').length,
      showcaseRune: slot14 && slot14.treatment === 'showcase_rune' ? 1 : 0
    };
  })();

  const openPack = async (setCode) => {
    setError(null);
    setLoading(true);
    setLastSet(setCode);
    setRevealedSlots(new Set()); // reset reveals for new pack
    try {
      const result = await postJson('/api/packs/open', { set: setCode });
      setSlots(result.slots || []);
      setSessionCards((prev) => [...prev, ...(result.slots || []).map((s) => ({ ...s.card, treatment: s.treatment }))]);
      if ((result.slots || []).length > 0) {
        setShowDramatic(true);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const revealSlot = useCallback((slotNum) => {
    setRevealedSlots((prev) => {
      const next = new Set(prev);
      next.add(slotNum);
      return next;
    });
  }, []);

  const revealAll = useCallback(() => {
    if (!orderedSlots.length) return;
    // Stagger reveals for drama: commons fast, rares slower
    const delay = (slot) => {
      if (slot <= 7) return (slot - 1) * 60;         // commons: quick cascade
      if (slot <= 10) return 420 + (slot - 8) * 80;  // uncommons
      if (slot <= 13) return 660 + (slot - 11) * 150; // rares: slower
      return 1110;                                     // rune last
    };
    orderedSlots.forEach((s) => {
      setTimeout(() => revealSlot(s.slot), delay(s.slot));
    });
  }, [orderedSlots, revealSlot]);

  const handleCloseReveal = () => {
    setShowDramatic(false);
  };

  useEffect(() => {
    if (!showDramatic) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleCloseReveal();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showDramatic]);

  const addToCollection = async (card, treatment) => {
    try {
      const isFoil = treatment === 'foil' || treatment === 'showcase' || treatment === 'alt_art' || treatment === 'signed' || treatment === 'showcase_rune';
      const current = await fetchJson('/api/collection').catch(() => []);
      const entry = Array.isArray(current) ? current.find((e) => e.card_id === card.id) : null;
      const qty = entry?.quantity ?? 0;
      const foilQty = entry?.foil_quantity ?? 0;
      if (isFoil) {
        await putJson('/api/collection', { card_id: card.id, quantity: qty, foil_quantity: foilQty + 1 });
      } else {
        await putJson('/api/collection', { card_id: card.id, quantity: qty + 1, foil_quantity: foilQty });
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const grouped = groupCardsById(sessionCards);

  /** Render a single card cell — face-down with glow, or flipped to reveal */
  const renderCell = (s, inOverlay = true) => {
    const isShowcaseRune = s.slot === 14 && s.treatment === 'showcase_rune';
    const isRevealed = !inOverlay || revealedSlots.has(s.slot);

    return (
      <div
        key={`${s.slot}-${s.card?.id}`}
        className={`po-cell${isRevealed ? ' po-cell--revealed' : ''}${s.treatment ? ` treatment-${s.treatment}` : ''}${isShowcaseRune ? ' showcase-rune' : ''}`}
        onClick={inOverlay && !isRevealed ? () => revealSlot(s.slot) : undefined}
      >
        <div className={`po-card-flip${isRevealed ? ' flipped' : ''}`}>
          {/* Back face — card back with treatment glow */}
          <div className={`po-card-face po-card-back ${glowClass(s.treatment)}`}>
            <img src={CARD_BACK} alt="Card back" className="po-card-back-img" draggable="false" />
          </div>
          {/* Front face — the actual card */}
          <div className="po-card-face po-card-front">
            <CardTile
              card={s.card}
              treatment={s.treatment}
              className={`po-tile${isShowcaseRune ? ' showcase-rune-tile' : ''}`}
              imageWrapClassName="pack-card-image-wrap"
              onClick={isRevealed ? () => setSelectedCard(s.card) : undefined}
              onRightClick={isRevealed ? () => addToCollection(s.card, s.treatment) : undefined}
              showPrice={showPrices}
              pricePeriod={pricePeriod}
            />
          </div>
        </div>
      </div>
    );
  };

  // ---- Tally chips ----
  const tallyChips = [];
  if (packTally) {
    if (packTally.slot11Treatment && packTally.slot11Treatment !== 'foil') {
      tallyChips.push({ label: treatmentLabel(packTally.slot11Treatment), cls: `treatment-${packTally.slot11Treatment}` });
    }
    if (packTally.signed) tallyChips.push({ label: `Signed ×${packTally.signed}`, cls: 'treatment-signed' });
    if (packTally.showcase) tallyChips.push({ label: `Showcase ×${packTally.showcase}`, cls: 'treatment-showcase' });
    if (packTally.altArt) tallyChips.push({ label: `Alt Art ×${packTally.altArt}`, cls: 'treatment-alt_art' });
    if (packTally.epic) tallyChips.push({ label: `Epic ×${packTally.epic}`, cls: 'treatment-epic' });
    if (packTally.showcaseRune) tallyChips.push({ label: 'Showcase Rune', cls: 'treatment-showcase_rune' });
  }

  // ---- LANDING: no pack opened yet ----
  if (!slots || slots.length === 0) {
    return (
      <div className="pack-opening-view">
        {selectedCard && (
          <CardDetailModal card={selectedCard} onClose={() => setSelectedCard(null)} />
        )}
        <div className="po-landing">
          <h1 className="po-landing-title">Open a Pack</h1>
          <p className="po-landing-sub">Choose a set to tear open</p>
          <div className="po-packs-row">
            {OPENABLE_SETS.map((s) => (
              <button
                key={s.code}
                type="button"
                className={`po-pack-btn${loading ? ' disabled' : ''}`}
                disabled={loading}
                onClick={() => openPack(s.code)}
              >
                <img src={s.img} alt={s.label} className="po-pack-img" draggable="false" />
                <span className="po-pack-label">{s.label}</span>
              </button>
            ))}
          </div>
          {error && <p className="gallery-error">{error}</p>}
        </div>
      </div>
    );
  }

  // ---- REVEAL + RESULTS ----
  return (
    <div className="pack-opening-view">
      {selectedCard && (
        <CardDetailModal card={selectedCard} onClose={() => setSelectedCard(null)} />
      )}

      {/* ---- Dramatic overlay ---- */}
      {showDramatic && orderedSlots.length > 0 && (
        <div
          className="po-overlay"
          role="dialog"
          aria-modal="true"
          onClick={(e) => { if (e.target === e.currentTarget) handleCloseReveal(); }}
        >
          <div className="po-overlay-inner">
            <button type="button" className="modal-close" aria-label="Close" onClick={handleCloseReveal}>×</button>

            {/* Top bar */}
            <div className="po-overlay-topbar">
              <div className="po-overlay-actions">
                {OPENABLE_SETS.map((s) => (
                  <button
                    key={s.code}
                    type="button"
                    className={`po-open-btn${s.code === lastSet ? ' po-open-btn--active' : ''}`}
                    disabled={loading}
                    onClick={() => openPack(s.code)}
                  >
                    {loading && s.code === lastSet ? 'Opening\u2026' : `Open ${s.label}`}
                  </button>
                ))}
                {!allRevealed && (
                  <button type="button" className="po-reveal-all-btn" onClick={revealAll}>
                    Reveal All
                  </button>
                )}
              </div>
              {tallyChips.length > 0 && allRevealed && (
                <div className="po-tally-chips">
                  {tallyChips.map((c, i) => (
                    <span key={i} className={`po-chip ${c.cls}`}>{c.label}</span>
                  ))}
                </div>
              )}
            </div>

            {/* 5-5-3+1 grid */}
            <div className="po-grid">
              <div className="po-row po-row--5">{row1.map((s) => renderCell(s, true))}</div>
              <div className="po-row po-row--5">{row2.map((s) => renderCell(s, true))}</div>
              <div className="po-row po-row--bottom">
                <div className="po-row po-row--3">{row3.map((s) => renderCell(s, true))}</div>
                <div className="po-rune-divider" />
                {slotRune && <div className="po-row po-row--1">{renderCell(slotRune, true)}</div>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---- Below overlay: pack selection + results ---- */}
      {!showDramatic && (
        <>
          <div className="po-header">
            <h1>Pack Opening</h1>
            <div className="po-packs-row po-packs-row--small">
              {OPENABLE_SETS.map((s) => (
                <button
                  key={s.code}
                  type="button"
                  className={`po-pack-btn po-pack-btn--small${loading ? ' disabled' : ''}`}
                  disabled={loading}
                  onClick={() => openPack(s.code)}
                >
                  <img src={s.img} alt={s.label} className="po-pack-img" draggable="false" />
                  <span className="po-pack-label">{s.label}</span>
                </button>
              ))}
            </div>
          </div>

          {error && <p className="gallery-error">{error}</p>}

          {/* Last pack grid — always revealed */}
          <section className="po-last-pack">
            <div className="po-section-head">
              <h2>Last Pack</h2>
              {tallyChips.length > 0 && (
                <div className="po-tally-chips">
                  {tallyChips.map((c, i) => (
                    <span key={i} className={`po-chip ${c.cls}`}>{c.label}</span>
                  ))}
                </div>
              )}
            </div>
            <div className="po-grid po-grid--compact">
              <div className="po-row po-row--5">{row1.map((s) => renderCell(s, false))}</div>
              <div className="po-row po-row--5">{row2.map((s) => renderCell(s, false))}</div>
              <div className="po-row po-row--bottom">
                <div className="po-row po-row--3">{row3.map((s) => renderCell(s, false))}</div>
                <div className="po-rune-divider" />
                {slotRune && <div className="po-row po-row--1">{renderCell(slotRune, false)}</div>}
              </div>
            </div>
          </section>

          {/* Session history */}
          {sessionCards.length > 0 && (
            <section className="po-session">
              <h2>Opened this session ({sessionCards.length} cards)</h2>
              <div className="cards-grid">
                {grouped.map(({ card, count }) => (
                  <CardTile
                    key={card.id}
                    card={card}
                    treatment={card.treatment}
                    quantity={count}
                    onClick={() => setSelectedCard(card)}
                    onRightClick={() => addToCollection(card, card.treatment)}
                    showPrice={showPrices}
                    pricePeriod={pricePeriod}
                  />
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
